import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, FormArray, Validators } from '@angular/forms';
import { LookupService } from '../../../../services/lookup.service';
import {
  DialogService,
} from '../../../../shared/services/dialog.service';
import { TimesheetService } from '../../../../services/timesheet.service';
import { ErrorAlertService } from '../../../../shared/services/error-alert.service';
import { CommonModule } from '@angular/common';
import { UserService } from '../../../../services/user.service';
import { ButtonComponent } from '@ppl/ui';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { DateWithDayOfWeekPipe } from "../../../../services/dateWithDayOfWeek";
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { AllowListAuthStore } from '../../../../shared/authorization/allow-list-auth.store';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';

@Component({
  selector: 'ppl-timesheet-billing-adjustment-view',
  standalone: true,
  imports: [
    CommonModule,
    NgZorroModule,
    ButtonComponent,
    ReactiveFormsModule,
    NzDatePickerModule,
    FormsModule,
    DateWithDayOfWeekPipe
  ],
  templateUrl: './timesheet-billing-adjustment-view.component.html',
  styleUrl: './timesheet-billing-adjustment-view.component.css',
})
export class TimesheetBillingAdjustViewComponent implements OnInit {
  private dialogSvc = inject(DialogService);
  private allowListStore = inject(AllowListAuthStore);

  pageId = PAGE_CONSTANTS.STAFF_BILLING_ADJUSTMENT;
  isReadOnly = false;
  isPageRestrictionLoaded = false;

  @Output() onProcessComplete = new EventEmitter<any>();

  @Input() selectedBillings: any[] = [];

  selectedCheckBoxValue: any;
  adjustTimesheetForm!: FormGroup;

  actionButtons: TableRowActionsModel = new TableRowActions({ view: true });
  programName: any;
  billings: any;
  authorizations: any;
  reasonList: any;
  submitRequested = false;

  constructor(
    private timesheetService: TimesheetService,
    private errorAlertService: ErrorAlertService,
    private lookupService: LookupService,
    private userService: UserService,
    private fb: FormBuilder,
    private dateTimeFormatter: DateTimeFormatterService
  ) { }

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.reasonList = this.lookupService.getLookupValue('adjustment_reason');
    this.setPageAccess();
    this.setBillingConfig();
  }

  private setPageAccess() {
    const authConfig = this.allowListStore.getConfig();
    if (authConfig && !authConfig.pages[this.pageId]) {
      this.isReadOnly = true;
    }
    this.isPageRestrictionLoaded = true;
  }

  get billingEntries() {
    return this.adjustTimesheetForm.get('billingEntries') as FormArray;
  }

  setBillingConfig() {
    this.adjustTimesheetForm = this.fb.group({
      billingEntries: this.fb.array([]),
    });

    const ids = this.selectedBillings.map(e => e.tas_timesheetline_detail_id);

    this.timesheetService.getBillingDetails({ timesheetIDs: ids }, this.programName).subscribe((data: any) => {
      if (data.status === 200) {
        data.responsedata?.forEach((auth: any) => {
          const matchingBilling = this.selectedBillings.find(
            billing => billing.tas_timesheetline_detail_id === auth.id
          );
          if (matchingBilling) {
            matchingBilling.authorizations = auth.authorizations;
          }
        })
      }
    });

    this.selectedBillings.forEach((value, index, arr) => {
      value.timeIn = value?.time_in ? this.dateTimeFormatter.convertTo12HourFormat(value?.time_in) : null,
        value.timeOut = value?.time_out ? this.dateTimeFormatter.convertTo12HourFormat(value.time_out) : null,
        this.addBillingEntry(index, value);
    });
  }

  addBillingEntry(index: number, billing: any) {
    const timesheetEntry = this.fb.group({
      serviceDate: [{ value: billing.service_date, disabled: true }],
      serviceDescription: [{ value: billing.service_description, disabled: true }],
      serviceTimeIn: [{ value: billing.timeIn, disabled: true }],
      serviceTimeOut: [{ value: billing.timeOut, disabled: true }],
      mcoName: [billing.mco_name + ' / ' + billing.client_auth_number, Validators.required],
      reason: [billing.reason, Validators.required],
      splitId: [billing.splitId],
      mco: [billing.mco_name],
      currentMco: [billing.mco_name],
      authNumber: [billing.client_auth_number],
      timesheetId: [billing.tas_timesheetline_detail_id],
      billedUnits: [billing.billed_units]
    });
    this.billingEntries.insert(index + 1, timesheetEntry);
  }

  onAdjustClick() {
    if (this.isReadOnly) return;
    this.submitRequested = true;
    let isValid = true;
    if (this.adjustTimesheetForm.valid) {
      const payload: any[] = [];
      this.billingEntries.controls.forEach(entry => {
        const timesheetId = entry.get('timesheetId')?.value;
        const billedUnits = entry.get('billedUnits')?.value;
        const currentMco = entry.get('mcoName')?.value;
        const mco = entry.get('mcoName')?.value;
        const mcoAuth = mco?.split(' / ');
        const allAuths = this.getAuthorizations(timesheetId);
        const requiredAuth = allAuths.find(
          (auth: any) => mcoAuth[1] === auth.clientAuthNum
        );

        if (this.validateUnits(requiredAuth, billedUnits)) {
          payload.push({
            timesheetId: timesheetId,
            mco: requiredAuth?.currentMCO,
            splitId: requiredAuth?.splitId,
            reason: entry.get('reason')?.value
          });
        } else {
          isValid = false;
          entry.get('mcoName')?.setErrors({ customError: 'MCO does not have sufficient units.' });
          return;
        }
      });

      if (isValid) {
        this.timesheetService.adjustBillings(payload, this.programName).subscribe((data: any) => {
          this.onProcessComplete.emit({});
        });
      }
    }
  }

  getAuthorizations(timesheetId: number) {
    const billing = this.selectedBillings.find(
      item => item.tas_timesheetline_detail_id === timesheetId
    );

    return billing?.authorizations || [];
  }

  selectionChanged(event: any) {
    this.selectedCheckBoxValue = event;
  }

  validateUnits(auth: any, billedUnits: number) {
    if (auth == null) {
      return false;
    }

    return auth.units - (auth.usedUnits + billedUnits) > 0;
  }

  rowClickedTs(event: any) {
  }

  rowClicked(event: any) {
  }

  onCancel() {
  }
}
